import BaseCtrl from 'edge/platform/base/BaseCtrl';
import assert from 'assert';
import _ from 'lodash';
import ApplicationFeatures from 'gw-portals-featuredetection-js/ApplicationFeatures';

class Tile {
    constructor(displaykey, icon, color) {
        this._displaykey = displaykey;
        this._icon = icon;
        this._color = color;
    }

    getDisplaykey() {
        return this._displaykey;
    }

    getColor() {
        return this._color;
    }

    getIcon() {
        return this._icon;
    }
}

class Filter {
    constructor(tile) {
        assert(tile instanceof Tile);

        this._tile = tile;
        this._isActive = false;

        const that = this;
        this._patchLoadMethod = _.memoize((producerCode, loadMethod) => { // Need to memoize, otherwise watcher looking for loadMethod would fire in an infinite loop
            return !loadMethod ? undefined : function () {
                return loadMethod.apply(this, arguments).then(
                    data => {
                        that._firstLoadDeferred.resolve();
                        return data;
                    },
                    err => {
                        // Reject the promise first load promise, rethrow.
                        that._firstLoadDeferred.reject();
                        throw err;
                    }
                );
            };
        });
    }

    getTile() {
        return this._tile;
    }

    static _getLoadMethod() {
        throw new Error('Should be overwritten by child class');
    }

    getLoadMethod() {
        // Need the producer code to memoize against, because function body
        // may be the same coming from the load method, so is not suitable for the unique key
        return this._patchLoadMethod(this._producerCode, this._getLoadMethod());
    }

    static _updateLoaders() {
        throw new Error('Should be overwritten by child class');
    }

    updateLoaders(producerCode, firstLoadDeferred) {
        this._producerCode = producerCode;
        this._firstLoadDeferred = firstLoadDeferred;
        return this._updateLoaders(producerCode);
    }

    setActive() {
        this._isActive = true;
    }

    setInactive() {
        this._isActive = false;
    }

    isActive() {
        return this._isActive;
    }
}

class QuickFilters {
    constructor() {
        this._filters = _.toArray(arguments);
        assert(this._filters.every(filter => filter instanceof Filter));

        const firstLoadDeferred = {};
        this._firstLoadDeferred = firstLoadDeferred;
        this._firstLoadPromise = new Promise((resolve, reject) => {
            firstLoadDeferred.resolve = _.once(resolve);
            firstLoadDeferred.reject = _.once(reject);
        });

        this._producerCode = null;

        this.setActive(this._filters[0]);
    }

    getFirstLoadPromise() {
        return this._firstLoadPromise;
    }

    getFilters() {
        return this._filters;
    }

    getActive() {
        return _.find(this._filters, filter => filter.isActive());
    }

    setActive(filter) {
        assert(this._filters.indexOf(filter) > -1);
        this._filters.forEach(f => f.setInactive());

        filter.setActive();
        this._updateCurrentFilterLoaders();
    }

    updateProducerCode(producerCode) {
        this._producerCode = producerCode;
        this._updateCurrentFilterLoaders();
    }

    _updateCurrentFilterLoaders() {
        if (this._producerCode) {
            this.getActive().updateLoaders(this._producerCode, this._firstLoadDeferred);
        }
    }
}

const Controller = function ($scope, ClaimService, gatewayParamConfig, InsuranceSuiteMetadata) {

    const causeTypelistPSA = InsuranceSuiteMetadata.get('cc').types.getTypelist('Causes_PSA'); // PSA
    const defaultTypelistPSA = InsuranceSuiteMetadata.get('cc').types.getTypelist('Default_PSA'); // PSA
    const makeTypelistPSA = InsuranceSuiteMetadata.get('cc').types.getTypelist('VehicleManufacturer'); // PSA
    const policySubTypelistPSA = InsuranceSuiteMetadata.get('cc').types.getTypelist('PolicySubType_PSA'); // PSA
    const lossCauseTypelistPSA = InsuranceSuiteMetadata.get('cc').types.getTypelist('LossCause'); // PSA

    function processClaims(claims) {
        const maxNumberOfResults = claims.maxNumberOfResults;
        claims = Array.isArray(claims) ? claims : claims.claims;

        $scope.showWholeList = false; // Needed for all filters except recently viewed
        $scope.repairerOnlyPSA = !ApplicationFeatures.hasFeature('role.producer') && ApplicationFeatures.hasFeature('role.vendor'); // PSA

        return {
            'items': claims.map(claim => ({
                'status': claim.state,
                'isDraft': claim.stateCode === 'draft',
                'product': claim.product,
                'claimNumber': claim.claimNumber,
                'lossDate': claim.dateOfLoss,

                'coverageType_PSA': claim.coverageTypeName_PSA, // PSA
                'policySubType_PSA': (claim.policySubType_PSA ? policySubTypelistPSA.getCode(claim.policySubType_PSA).name : "-"), // PSA
                'cause_PSA': (claim.cause_PSA ? causeTypelistPSA.getCode(claim.cause_PSA).name : "-"), // PSA
                'default_PSA': (claim.default_PSA ? defaultTypelistPSA.getCode(claim.default_PSA).name : "-"), // PSA
                'description_PSA': claim.description_PSA, // PSA
                'make_PSA': (claim.firstVehicleMake_PSA ? makeTypelistPSA.getCode(claim.firstVehicleMake_PSA).name : "-"), // PSA
                'model_PSA': claim.firstVehicleModel_PSA,
                'canViewPolicy_PSA': claim.canViewPolicy_PSA, // PSA
                'lossCause_PSA': (claim.lossCause_PSA ? lossCauseTypelistPSA.getCode(claim.lossCause_PSA).name : "-"), // PSA

                'policyNumber': claim.policyNumber,
                'accountNumber': claim.accountNumber,
                'accountIsPersonal': claim.isPersonalAccount,
                'accountName': claim.insuredName,
                'totalPayments': claim.totalPayments,
                'totalIncurredNet': claim.totalIncurredNet
            })),
            'maxNumberOfResults': maxNumberOfResults
        };
    }

    class RecentlyViewedFilter extends Filter {
        constructor() {
            super(new Tile('gateway.views.claims-landing.Recently Viewed', 'fa-eye', 'gw-tile-color-8'));
        }

        _updateLoaders() {
            this._load = function () {
                // Producer code is irrelevant here
                return ClaimService
                    .getRecentlyViewedClaims()
                    .then(processClaims)
                    .then(data => {
                        $scope.showWholeList = true; // There's no pagination on recently viewed
                        data.maxNumberOfResults = data.items.length;
                        return data;
                    });
            };
        }

        _getLoadMethod() {
            return this._load;
        }
    }

    class RecentlyCreatedFilter extends Filter {
        constructor() {
            super(new Tile('gateway.views.claims-landing.Recently Created', 'fa-calendar', 'gw-tile-color-8'));
        }

        _updateLoaders(producerCode) {
            this._load = function (offsetStart, offsetEnd) {
                const queryOptions = {
                    offsetStart: offsetStart,
                    offsetEnd: offsetEnd,
                    orderBy: 'CreateTime',
                    orderByDescending: true
                };

                const getClaims = producerCode === 'all' ? ClaimService.getClaimsForCurrentUser(gatewayParamConfig.claimsCreatedInLastXDays, queryOptions) : ClaimService.getClaimsForProducerCode(gatewayParamConfig.claimsCreatedInLastXDays, queryOptions, producerCode);

                return getClaims.then(processClaims);
            };
        }

        _getLoadMethod() {
            return this._load;
        }
    }

    class AllOpenFilter extends Filter {
        constructor() {
            super(new Tile('gateway.views.claims-landing.Open Claims', 'fa-folder-open-o', 'gw-tile-color-8'));
        }

        _updateLoaders(producerCode) {
            this._load = function (offsetStart, offsetEnd) {
                const queryOptions = {
                    offsetStart: offsetStart,
                    offsetEnd: offsetEnd,
                    orderBy: 'CreateTime',
                    orderByDescending: true
                };

                const getClaims = producerCode === 'all' ? ClaimService.getOpenClaimsForCurrentUser(gatewayParamConfig.claimsCreatedInLastXDays, queryOptions) : ClaimService.getOpenClaimsForProducerCode(gatewayParamConfig.claimsCreatedInLastXDays, queryOptions, producerCode);

                return getClaims.then(processClaims);
            };
        }

        _getLoadMethod() {
            return this._load;
        }
    }

    class RecentlyClosedFilter extends Filter {
        constructor() {
            super(new Tile('gateway.views.claims-landing.Closed Claims', 'fa-lock', 'gw-tile-color-8'));
        }

        _updateLoaders(producerCode) {
            this._load = function (offsetStart, offsetEnd) {
                const queryOptions = {
                    offsetStart: offsetStart,
                    offsetEnd: offsetEnd,
                    orderBy: 'CreateTime',
                    orderByDescending: true
                };

                const getClaims = producerCode === 'all' ? ClaimService.getClosedClaimsForCurrentUser(gatewayParamConfig.claimsCreatedInLastXDays, queryOptions) : ClaimService.getClosedClaimsForProducerCode(gatewayParamConfig.claimsCreatedInLastXDays, queryOptions, producerCode);

                return getClaims.then(processClaims);
            };
        }

        _getLoadMethod() {
            return this._load;
        }
    }

    const ctrl = Object.create(BaseCtrl);

    $scope.quickFilters = new QuickFilters(
        new RecentlyViewedFilter(),
        new RecentlyCreatedFilter(),
        new AllOpenFilter(),
        new RecentlyClosedFilter()
    );

    $scope.initialLoading = $scope.quickFilters.getFirstLoadPromise();

    $scope.selectedProducerCode = null; // For producer code select
    $scope.$watch('selectedProducerCode', _.after(2, producerCode => $scope.quickFilters.updateProducerCode(producerCode.code)));

    $scope.pageSize = gatewayParamConfig.claimsLandingResultsPerPage;

    return ctrl.create($scope);
};

Controller.$inject = ['$scope', 'claim.ClaimService', 'gatewayParamConfig', 'InsuranceSuiteMetadata'];
export default Controller;
